import { createTheme, ThemeOptions } from '@mui/material/styles';

const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: '#7C8B6F', // Sage Green
      light: mode === 'light' ? '#A4B494' : '#9DAB8E',
      dark: mode === 'light' ? '#5D6B52' : '#6A7760',
      contrastText: '#ffffff',
    },
    secondary: {
      main: mode === 'light' ? '#8B7C8B' : '#A394A3', // Muted Purple - complements sage green
      light: mode === 'light' ? '#B3A4B3' : '#C4B7C4',
      dark: mode === 'light' ? '#6B5D6B' : '#7C6F7C',
      contrastText: '#ffffff',
    },
    success: {
      main: mode === 'light' ? '#7C8B6F' : '#9DAB8E',
      light: mode === 'light' ? '#A4B494' : '#B5C4A7',
      dark: mode === 'light' ? '#5D6B52' : '#6A7760',
    },
    error: {
      main: mode === 'light' ? '#C17B7B' : '#D99A9A', // Muted red that harmonizes
      light: mode === 'light' ? '#D99A9A' : '#E5B5B5',
      dark: mode === 'light' ? '#A56565' : '#B88585',
    },
    warning: {
      main: mode === 'light' ? '#C1A67B' : '#D9BF9A', // Warm tan/beige
      light: mode === 'light' ? '#D9BF9A' : '#E5CDB5',
      dark: mode === 'light' ? '#A58965' : '#B89E85',
    },
    info: {
      main: mode === 'light' ? '#7B9EC1' : '#9AB8D9', // Soft blue
      light: mode === 'light' ? '#9AB8D9' : '#B5CAE5',
      dark: mode === 'light' ? '#6585A5' : '#8599B8',
    },
    text: {
      primary: mode === 'light' ? '#2C3E2D' : '#E8F0E8',
      secondary: mode === 'light' ? '#5D6B52' : '#B8C7B8',
    },
    background: {
      default: mode === 'light' ? '#FAFBFA' : '#0F1410',
      paper: mode === 'light' ? '#ffffff' : '#1A1F1A',
    },
    divider: mode === 'light' ? 'rgba(124, 139, 111, 0.12)' : 'rgba(157, 171, 142, 0.12)',
    grey: {
      50: mode === 'light' ? '#FAFBFA' : '#141914',
      100: mode === 'light' ? '#F0F4F0' : '#1A1F1A',
      200: mode === 'light' ? '#E0E8E0' : '#242924',
      300: mode === 'light' ? '#C8D4C8' : '#2E342E',
      400: mode === 'light' ? '#A4B494' : '#4A524A',
    }
  },
  typography: {
    fontFamily: [
      'IBM Plex Sans',
      'DM Sans',
      'Manrope',
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.015em',
    },
    subtitle1: {
      letterSpacing: 0,
    },
    subtitle2: {
      letterSpacing: 0,
      fontWeight: 500,
    },
    body1: {
      letterSpacing: 0,
    },
    body2: {
      letterSpacing: 0,
    },
  },
  shape: {
    borderRadius: 12,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: mode === 'light' 
              ? '0 2px 8px 0 rgba(124, 139, 111, 0.3)'
              : '0 2px 8px 0 rgba(157, 171, 142, 0.4)',
          },
        },
        outlined: {
          borderWidth: 1.5,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: mode === 'light' 
            ? '0 2px 20px 0 rgba(124, 139, 111, 0.08)'
            : '0 2px 20px 0 rgba(157, 171, 142, 0.15)',
          backgroundColor: mode === 'light' ? '#ffffff' : '#1A1F1A',
          backgroundImage: mode === 'light'
            ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 1))'
            : 'none',
          backdropFilter: 'blur(8px)',
          border: mode === 'light'
            ? '1px solid rgba(124, 139, 111, 0.08)'
            : '1px solid rgba(157, 171, 142, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#7C8B6F' : '#1A1F1A',
          backgroundImage: 'none',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${mode === 'light' ? 'rgba(124, 139, 111, 0.2)' : 'rgba(157, 171, 142, 0.1)'}`,
          boxShadow: 'none',
          '& .MuiToolbar-root': {
            color: mode === 'light' ? '#ffffff' : '#E8F0E8',
          },
          '& .app-logo': {
            color: mode === 'light' ? '#ffffff' : '#E8F0E8',
          },
          '& .app-title': {
            color: mode === 'light' ? '#ffffff' : '#E8F0E8',
            fontWeight: 600,
            letterSpacing: '-0.015em',
          },
          '& .MuiIconButton-root': {
            color: mode === 'light' ? '#ffffff' : '#E8F0E8',
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(232, 240, 232, 0.08)',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? '#7C8B6F' : '#1A1F1A',
          backgroundImage: 'none',
          backdropFilter: 'blur(8px)',
          borderRight: `1px solid ${mode === 'light' ? 'rgba(124, 139, 111, 0.2)' : 'rgba(157, 171, 142, 0.1)'}`,
          '& .MuiListItemButton-root, & .MuiListItem-root': {
            borderRadius: 8,
            margin: '4px 8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(232, 240, 232, 0.15)',
              transform: 'translateX(4px)',
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'light' 
                ? 'rgba(255, 255, 255, 0.25)'
                : 'rgba(232, 240, 232, 0.25)',
              '& .MuiListItemText-primary': {
                color: mode === 'light' ? '#ffffff' : '#E8F0E8',
                fontWeight: 600,
              },
              '& .MuiListItemIcon-root': {
                color: mode === 'light' ? '#ffffff' : '#E8F0E8',
              },
              '&:hover': {
                backgroundColor: mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.35)'
                  : 'rgba(232, 240, 232, 0.35)',
              },
            },
          },
          '& .MuiListItemIcon-root': {
            color: mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(232, 240, 232, 0.7)',
            minWidth: 40,
          },
          '& .MuiListItemText-primary': {
            color: mode === 'light' ? '#ffffff' : '#E8F0E8',
            fontWeight: 500,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#ffffff' : '#1A1F1A',
          backdropFilter: 'blur(8px)',
        },
      },
      variants: [
        {
          props: { variant: 'elevation' },
          style: {
            boxShadow: mode === 'light' 
              ? '0 2px 20px 0 rgba(124, 139, 111, 0.08)'
              : '0 2px 20px 0 rgba(157, 171, 142, 0.15)',
            border: mode === 'light'
              ? '1px solid rgba(124, 139, 111, 0.08)'
              : '1px solid rgba(157, 171, 142, 0.08)',
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            borderWidth: 1.5,
            borderColor: mode === 'light' ? 'rgba(124, 139, 111, 0.2)' : 'rgba(157, 171, 142, 0.2)',
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? 'rgba(124, 139, 111, 0.12)' : 'rgba(157, 171, 142, 0.12)'}`,
          padding: '16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: mode === 'light' ? 'rgba(124, 139, 111, 0.04)' : 'rgba(157, 171, 142, 0.04)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        outlined: {
          borderWidth: 1.5,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderWidth: 1.5,
              borderColor: mode === 'light' ? 'rgba(124, 139, 111, 0.3)' : 'rgba(157, 171, 142, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: mode === 'light' ? 'rgba(124, 139, 111, 0.5)' : 'rgba(157, 171, 142, 0.5)',
            },
          },
        },
      },
    },
  },
});

export const createAppTheme = (darkMode: boolean) => {
  return createTheme(getThemeOptions(darkMode ? 'dark' : 'light'));
}; 